'use client'

import { ArrowLeftIcon } from 'lucide-react'
import Link from 'next/link'

import {
  Separator,
  SidebarContent,
  SidebarItem,
  SidebarList,
} from '@/components/ui'
import type { User } from '@/services/http/usersService/types'
import { generateRoutePath } from '@/utils/routes/generateRoutePath'
import type { MenuItem } from '@/utils/routes/menu'
import { MENU_EXTERNAL_ITEMS } from '@/utils/routes/menu'

type ExternalMenuProps = {
  activeMenu: string
  isCollapsed: boolean
  pathname: string
  selectedMenu?: MenuItem
  user: User | null
}

export const ExternalMenu = ({
  activeMenu,
  isCollapsed,
  pathname,
  selectedMenu,
  user,
}: ExternalMenuProps) => {
  return (
    <SidebarContent>
      <SidebarList className="h-full gap-3">
        <SidebarItem asChild>
          <Link href={generateRoutePath('home')}>
            {isCollapsed ? (
              <ArrowLeftIcon className="size-4 text-system-primary-icon md:size-5" />
            ) : (
              <>
                <ArrowLeftIcon className="size-4 text-system-primary-icon md:size-5" />
                <span className="text-sm font-semibold text-system-text-secondary md:text-base">
                  {selectedMenu?.label}
                </span>
              </>
            )}
          </Link>
        </SidebarItem>

        <Separator />

        {MENU_EXTERNAL_ITEMS.find((item) => item.key === activeMenu)?.items.map(
          ({
            icon: Icon,
            label,
            link,
            disabled,
            matchPattern,
            subItems,
            allowedEmails,
          }) => {
            const hasSubItems = subItems && subItems.length > 0
            if (
              allowedEmails &&
              user?.email &&
              !allowedEmails.includes(user.email)
            )
              return null

            return (
              <div key={link}>
                <SidebarItem
                  isActive={
                    !hasSubItems && new RegExp(matchPattern).test(pathname)
                  }
                  disabled={disabled}
                  isCollapsed={isCollapsed}
                  asChild
                >
                  <Link href={link}>
                    {isCollapsed ? (
                      Icon && <Icon className="size-5 md:size-6" />
                    ) : (
                      <>
                        {Icon && <Icon className="size-5 md:size-6" />}
                        {label}
                      </>
                    )}
                  </Link>
                </SidebarItem>

                {subItems && subItems.length > 0 && (
                  <div className="ml-4 border-l border-gray-200">
                    {subItems.map(
                      ({
                        icon: SubIcon,
                        label,
                        link,
                        disabled,
                        matchPattern,
                      }) => (
                        <SidebarItem
                          key={link}
                          isActive={new RegExp(matchPattern).test(pathname)}
                          disabled={disabled}
                          isCollapsed={isCollapsed}
                          asChild
                        >
                          <Link href={link} className="pl-4">
                            {isCollapsed ? (
                              <SubIcon className="size-4 md:size-5" />
                            ) : (
                              <>
                                <SubIcon className="size-4 md:size-5" /> {label}
                              </>
                            )}
                          </Link>
                        </SidebarItem>
                      ),
                    )}
                  </div>
                )}
              </div>
            )
          },
        )}
      </SidebarList>
    </SidebarContent>
  )
}

ExternalMenu.displayName = 'ExternalMenu'
